import React, { useState } from 'react';
import { Popconfirm, Tooltip, Spin } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    DownOutlined,
    LoadingOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { BASE_URL } from '@/config/serverApiConfig';
import WorkItemContextMenu from './modals/WorkItemContextMenu';

const PRIORITY_STYLES = {
    Critical: 'bg-[#ffdad6] text-[#93000a] dark:bg-red-950/80 dark:text-red-300 dark:border dark:border-red-800/50',
    High: 'bg-red-50 text-red-600 dark:bg-red-950/60 dark:text-red-400 dark:border dark:border-red-800/40',
    Medium: 'bg-[#c9e6ff] text-[#004c6e] dark:bg-blue-950/80 dark:text-blue-300 dark:border dark:border-blue-800/50',
    Low: 'bg-[#e7e8e9] text-[#424750] dark:bg-slate-800 dark:text-slate-300 dark:border dark:border-slate-700',
};

const STATUS_STYLES = {
    Backlog: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:border dark:border-slate-700',
    'In Progress': 'bg-blue-50 text-blue-600 dark:bg-blue-950/70 dark:text-blue-300 dark:border dark:border-blue-800/50',
    'Under Review': 'bg-amber-50 text-amber-600 dark:bg-amber-950/70 dark:text-amber-300 dark:border dark:border-amber-800/50',
    Completed: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    Closed: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    Close: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    'On Hold': 'bg-orange-50 text-orange-600 dark:bg-orange-950/70 dark:text-orange-300 dark:border dark:border-orange-800/50',
    Cancelled: 'bg-red-50 text-red-400 dark:bg-red-950/50 dark:text-red-400 dark:border dark:border-red-800/40',
    Incomplete: 'bg-rose-50 text-rose-700 dark:bg-rose-950/80 dark:text-rose-300 dark:border dark:border-rose-800/60 font-black',
};

const STATUS_DOT = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    Closed: 'bg-emerald-400',
    Close: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
    Incomplete: 'bg-rose-500',
};

// Progress bar color per status — matches approved design
const EFFORT_BAR_COLOR = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    Closed: 'bg-emerald-400',
    Close: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
    Incomplete: 'bg-rose-500',
};

const STATUS_OPTIONS = ['Backlog', 'In Progress', 'Under Review', 'Completed', 'On Hold', 'Cancelled', 'Incomplete'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseEstimatedToMs(str) {
    if (!str) return 0;
    let ms = 0;
    const h = str.match(/(\d+(?:\.\d+)?)h/);
    const m = str.match(/(\d+)m/);
    if (h) ms += parseFloat(h[1]) * 3600000;
    if (m) ms += parseInt(m[1]) * 60000;
    return ms;
}

function formatMs(ms) {
    if (!ms || ms <= 0) return '0h';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// EST/EFFORT cell — matches the approved design exactly
// Shows "12h / 8.5h" with a small progress bar below
function EffortCell({ estimatedTime, actualTimeMs, status }) {
    const estimatedMs = parseEstimatedToMs(estimatedTime);
    const actual = actualTimeMs || 0;
    const pct = estimatedMs > 0 ? Math.min(Math.round((actual / estimatedMs) * 100), 100) : 0;
    const overrun = estimatedMs > 0 && actual > estimatedMs;
    const barColor = overrun ? 'bg-red-400' : (EFFORT_BAR_COLOR[status] || 'bg-[#28b5fd]');

    return (
        <div className="text-center min-w-[96px]">
            <div className="text-sm font-bold text-[#191c1d] dark:text-white">
                {estimatedTime || '—'}
                {actual > 0 && (
                    <>
                        {' / '}
                        <span className={`${overrun ? 'text-red-500' : 'text-[#006591] dark:text-[#28b5fd]'}`}>
                            {formatMs(actual)}
                        </span>
                    </>
                )}
            </div>
            {estimatedMs > 0 && (
                <div className="w-20 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mx-auto mt-1.5 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all ${barColor}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </div>
    );
}

function getInitials(name = '') {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

// Build full display name from name + surname fields (Admin model stores them separately)
// Returns the name in Title Case (first letter of each word capitalised).
function getFullName(person) {
    if (!person) return '';
    const parts = [person.name, person.surname].filter(Boolean);
    const full = parts.join(' ').trim() || person.name || '';
    return full.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolves an admin photo value to a fully-qualified URL.
 */
function AvatarCell({ assignee, isMilestone = false }) {
    const [imgError, setImgError] = useState(false);

    let photoUrl = null;
    if (assignee && typeof assignee === 'object') {
        if (assignee.photo) {
            photoUrl = assignee.photo.startsWith('http')
                ? assignee.photo
                : `${BASE_URL.replace(/\/api\/?$/, '')}/${assignee.photo.replace(/^\//, '')}`;
        }
    }

    React.useEffect(() => {
        setImgError(false);
    }, [photoUrl]);

    if (!assignee) {
        return (
            <div className="flex items-center gap-2">
                <div
                    className="rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-400 flex-shrink-0 overflow-hidden"
                    style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                >?</div>
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-[#424750] dark:text-slate-400">Unassigned</span>
                    {isMilestone && <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">Owner</span>}
                </div>
            </div>
        );
    }

    const fullName = getFullName(assignee);
    const showPhoto = photoUrl && !imgError;

    return (
        <div className="flex items-center gap-2">
            <div
                style={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    minHeight: 28,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: showPhoto ? 'transparent' : 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)',
                }}
            >
                {showPhoto
                    ? <img
                        src={photoUrl}
                        alt={fullName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '50%' }}
                        onError={() => setImgError(true)}
                    />
                    : getInitials(fullName)
                }
            </div>
            <div className="flex flex-col leading-tight">
                <span className="text-sm font-bold text-[#191c1d] dark:text-white truncate max-w-[130px]">
                    {fullName}
                </span>
                {isMilestone && (
                    <span className="text-[10px] font-extrabold text-amber-600 dark:text-amber-400">
                        Owner
                    </span>
                )}
            </div>
        </div>
    );
}

// Column definitions — order matches approved design: Title · Assignee · Project · Priority · Stage · EST/EFFORT · Due Date · Actions
const COLUMNS = [
    { key: 'title', label: 'Title / Task ID', align: 'left', rounded: 'tl' },
    { key: 'assignee', label: 'Assignee', align: 'left' },
    { key: 'project', label: 'Project', align: 'left' },
    { key: 'priority', label: 'Priority', align: 'left' },
    { key: 'stage', label: 'Stage', align: 'left' },
    { key: 'effort', label: 'Est/Effort', align: 'center' },
    { key: 'due', label: 'Due Date', align: 'left' },
    { key: 'actions', label: 'Actions', align: 'center', rounded: 'tr' },
];

export default function TaskListTable({
    tasks = [],
    loading = false,
    pagination = {},
    onEdit,
    onDelete,
    onStatusChange,
    onPageChange,
    onRowClick,
    onConvertType,
    onAddSubtask,
    updatingStatusId = null, // taskId currently having status updated
    deletingId = null,        // taskId currently being deleted
}) {
    const { total = 0, page = 1, totalPages = 1 } = pagination;

    if (loading) {
        return (
            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] overflow-hidden">
                <div className="flex items-center justify-center py-16 gap-3 text-[#424750] dark:text-slate-400">
                    <LoadingOutlined style={{ fontSize: 22 }} />
                    <span className="text-sm font-medium">Loading tasks…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f8fafc] dark:bg-[#262626] border-b border-slate-100 dark:border-[#303030]">
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`px-6 py-4 font-headline text-xs font-bold uppercase tracking-wider text-[#64748b] dark:text-[#94a3b8]
                                        ${col.align === 'center' ? 'text-center' : ''}
                                        ${col.rounded === 'tl' ? 'rounded-tl-xl' : ''}
                                        ${col.rounded === 'tr' ? 'rounded-tr-xl' : ''}
                                    `}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {tasks.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length} className="px-6 py-16 text-center">
                                    <div className="flex flex-col items-center gap-3 text-[#424750] dark:text-slate-400">
                                        <UnorderedListOutlined style={{ fontSize: 36 }} />
                                        <p className="text-sm font-medium">No work items found.</p>
                                        <p className="text-xs">Click "+ New Task" to create your first item.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            tasks.map((task, idx) => {
                                const isMilestone = task.itemType === 'milestone';
                                const isChildTask = Boolean(task.parentId || task.milestoneId || task.itemType === 'subtask');

                                return (
                                    <tr
                                        key={task._id}
                                        className={`transition-all hover:-translate-y-px border-b border-slate-100 dark:border-[#303030]
                                            ${idx % 2 !== 0 ? 'bg-[#f3f4f5]/30 dark:bg-[#141414]' : 'bg-white dark:bg-[#1f1f1f]'}
                                            hover:bg-[#f3f4f5] dark:hover:bg-[#262626]
                                        `}
                                    >
                                        {/* Task / Milestone Title — wrapped in Context Menu */}
                                        <td className="px-6 py-4 cursor-pointer" onClick={() => onRowClick?.(task)}>
                                            <WorkItemContextMenu
                                                item={task}
                                                onView={onRowClick}
                                                onEdit={onEdit}
                                                onStatusChange={onStatusChange}
                                                onAddSubtask={onAddSubtask}
                                                onConvertType={onConvertType}
                                                onDelete={onDelete}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {/* Work Item Type Icon: ◆ Diamond for Milestone vs ○ Circle for Task */}
                                                    {isMilestone ? (
                                                        <span className="text-amber-500 font-black text-lg shrink-0 leading-none" title="Milestone">
                                                            ◆
                                                        </span>
                                                    ) : (
                                                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || 'bg-slate-300'}`} title="Task" />
                                                    )}

                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-[#191c1d] dark:text-white text-base hover:text-[#03488a] dark:hover:text-[#a7c8ff] transition-colors">
                                                                {task.title}
                                                            </span>
                                                            {isMilestone && (
                                                                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                                                    Milestone
                                                                </span>
                                                            )}
                                                            {!isMilestone && isChildTask && (
                                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-950/80 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                                                    Child Task
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-[#737782] dark:text-slate-400 font-mono mt-0.5">{task.taskId}</p>
                                                    </div>
                                                </div>
                                            </WorkItemContextMenu>
                                        </td>

                                        {/* Assignee / Milestone Owner */}
                                        <td className="px-6 py-4">
                                            <AvatarCell
                                                assignee={isMilestone ? (task.milestoneOwner || task.assignee) : task.assignee}
                                                isMilestone={isMilestone}
                                            />
                                        </td>

                                        {/* Project */}
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-bold text-[#424750] dark:text-slate-300 bg-[#e7e8e9] dark:bg-[#334155] px-2.5 py-1 rounded-md">
                                                {task.project?.name || '—'}
                                            </span>
                                        </td>

                                        {/* Priority */}
                                        <td className="px-6 py-4">
                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium}`}>
                                                {task.priority}
                                            </span>
                                        </td>

                                        {/* Stage — inline select */}
                                        <td className="px-6 py-4">
                                            {(() => {
                                                const resolvedStatus = ['Closed', 'Close'].includes(task.status) ? 'Completed' : (task.status || 'Backlog');
                                                return updatingStatusId === task._id ? (
                                                    <div className="flex items-center gap-2">
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />
                                                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[resolvedStatus] || ''}`}>
                                                            {resolvedStatus}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={resolvedStatus}
                                                        onChange={(e) => { e.stopPropagation(); onStatusChange(task._id, e.target.value); }}
                                                        className={`text-xs font-bold px-3 py-1 rounded-full border-none cursor-pointer focus:ring-2 focus:ring-[#28b5fd] outline-none ${STATUS_STYLES[resolvedStatus] || ''}`}
                                                    >
                                                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                );
                                            })()}
                                        </td>

                                        {/* EST/EFFORT or Milestone Progress */}
                                        <td className="px-6 py-4">
                                            {isMilestone ? (
                                                <div className="text-center min-w-[100px]">
                                                    <div className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff]">
                                                        {task.completedChildTasks ?? 0} / {task.totalChildTasks ?? 0} Tasks ({task.progress ?? 0}%)
                                                    </div>
                                                    <div className="w-20 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mx-auto mt-1.5 overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all bg-emerald-500"
                                                            style={{ width: `${task.progress ?? 0}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <EffortCell
                                                    estimatedTime={task.estimatedTime}
                                                    actualTimeMs={task.actualTimeMs}
                                                    status={task.status}
                                                />
                                            )}
                                        </td>

                                        {/* Due Date */}
                                        <td className="px-6 py-4">
                                            {task.dueDate ? (
                                                <span className={`text-sm font-bold ${new Date(task.dueDate) < new Date() && !['Completed', 'Cancelled', 'Closed', 'Close'].includes(task.status) ? 'text-red-500' : 'text-[#191c1d] dark:text-white'}`}>
                                                    {dayjs(task.dueDate).format('MMM D, YYYY')}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-[#737782] dark:text-slate-500">—</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <Tooltip title={isMilestone ? "Edit milestone" : "Edit task"}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                                                        disabled={deletingId === task._id || updatingStatusId === task._id}
                                                        className="p-1.5 rounded-lg hover:bg-[#e7e8e9] dark:hover:bg-[#334155] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        <EditOutlined className="text-[#424750] dark:text-slate-300" style={{ fontSize: 16 }} />
                                                    </button>
                                                </Tooltip>

                                                <Popconfirm
                                                    title={isMilestone ? "Delete this milestone?" : "Delete this task?"}
                                                    description="This cannot be undone."
                                                    onConfirm={() => onDelete(task._id)}
                                                    okText="Delete"
                                                    okButtonProps={{
                                                        danger: true,
                                                        loading: deletingId === task._id,
                                                    }}
                                                    cancelText="Cancel"
                                                    placement="topRight"
                                                >
                                                    <Tooltip title="Delete">
                                                        <button
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={updatingStatusId === task._id}
                                                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                        >
                                                            {deletingId === task._id
                                                                ? <LoadingOutlined className="text-red-500" style={{ fontSize: 16 }} />
                                                                : <DeleteOutlined className="text-[#737782] dark:text-slate-400" style={{ fontSize: 16 }} />
                                                            }
                                                        </button>
                                                    </Tooltip>
                                                </Popconfirm>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {tasks.length > 0 && (
                <div className="px-6 py-4 bg-[#f3f4f5]/50 dark:bg-[#263347]/50 flex justify-between items-center border-t border-slate-100 dark:border-slate-700">
                    <p className="text-xs font-bold text-[#424750] dark:text-slate-400">
                        Showing {tasks.length} of {total} task{total !== 1 ? 's' : ''}
                    </p>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                            {/* Prev */}
                            <button
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center hover:bg-white dark:hover:bg-[#334155] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <LeftOutlined style={{ fontSize: 11 }} />
                            </button>

                            {/* Page number buttons — show up to 5, with ellipsis */}
                            {(() => {
                                const pages = [];
                                const delta = 2;
                                const left = Math.max(1, page - delta);
                                const right = Math.min(totalPages, page + delta);

                                if (left > 1) {
                                    pages.push(1);
                                    if (left > 2) pages.push('...');
                                }
                                for (let p = left; p <= right; p++) pages.push(p);
                                if (right < totalPages) {
                                    if (right < totalPages - 1) pages.push('...');
                                    pages.push(totalPages);
                                }

                                return pages.map((p, i) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-xs text-[#737782] dark:text-slate-500">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => onPageChange(p)}
                                            className="w-8 h-8 rounded-lg text-xs font-bold transition-all"
                                            style={page === p
                                                ? { background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)', color: '#fff' }
                                                : {}}
                                            {...(page !== p ? { className: 'w-8 h-8 rounded-lg text-xs font-bold transition-all text-[#424750] dark:text-slate-400 hover:bg-white dark:hover:bg-[#334155] border border-slate-200 dark:border-slate-600' } : {})}
                                        >
                                            {p}
                                        </button>
                                    )
                                );
                            })()}

                            {/* Next */}
                            <button
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= totalPages}
                                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center hover:bg-white dark:hover:bg-[#334155] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <RightOutlined style={{ fontSize: 11 }} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}




